<template>
  <div class="flex flex-col w-full">
    <div class="flex flex-col gap-[18px]">
      <div
        v-if="showSelection"
        class="flex flex-row items-center gap-2 mt-[2px]"
      >
        <CustomDropdown
          v-model="selectedPerPage"
          maxHeight="200px"
          gap="10px"
          position="bottom"
        >
          <template #trigger>
            <div class="flex items-center gap-2 mx-3 cursor-pointer">
              <span class="text-sm">{{ selectedCount }} selected</span>
              <ThemeImage
                :icon="imageNames.BOTTOM_ARROW"
                alt="bottom_white_arrow"
                class="size-4"
              />
            </div>
          </template>

          <template #default>
            <div class="dropdown_item h-10" @click="selectThisPage">
              Select this Page
            </div>

            <div
              v-if="selectAllEnabled"
              class="dropdown_item h-10"
              @click="selectAll"
            >
              Select all ({{ pagination.total }})
            </div>

            <div class="dropdown_item h-10" @click="unselectAll">
              Unselect All
            </div>
          </template>
        </CustomDropdown>

        <AddToListDropdown
          v-if="showAddToListDropdown"
          :source="source"
          :entity-lists="entityLists"
          :show-entity-create="showEntityCreate"
          @entity-list-created="toAddEntityLists"
          @update:show-entity-create="showEntityCreate = $event"
        />

        <RemoveFromList v-if="listId" @remove="toRemoveEntityListItems" />

        <AddToSequenceDropdown
          v-if="showAddToSequenceDropdown"
          :source="source"
          @sequence-selected="addPeopleToSequence"
          @create-sequence="handleSequenceCreated"
        />

        <AddToDialerCampaign
          v-if="showAddToDialerCampaign"
          :source="source"
          :selected-entities="selectedEntities"
          @success="notifySuccess"
          @error="notifyError"
        />

        <slot name="selection-actions" />

        <AssignOwnerDropdown
          v-if="showAssignOwnerDropdown"
          :source="source"
          :selection="selection"
          :select-count="selectedCount"
          :view-id="viewId"
          @update:success="handleAssignOwner"
          :list-id="listId"
        />

        <BulkAssignTasks
          v-if="showAddTasksDropdown"
          :source="source"
          :selection="selection"
          :select-count="selectedCount"
          :view-id="viewId"
          @bulk-assigned-tasks="unselectAll"
          :list-id="listId"
        />

        <BulkAssignNotes
          v-if="showAddNotesDropdown"
          :source="source"
          :selection="selection"
          :select-count="selectedCount"
          :view-id="viewId"
          @bulk-assigned-notes="unselectAll"
          :list-id="listId"
        />

        <template v-if="showEmailExport">
          <a-button
            class="secondary_small gap-1 flex items-center justify-center"
            @click="handleEmailExport"
          >
            <ThemeImage
              :icon="imageNames.EXPORT_GREY"
              alt="export icon"
              class="size-4"
            />
            Export
          </a-button>
        </template>

        <a-button
          v-if="
            props.source === entityTypes.SEQUENCE_ENTITY &&
            props.showRemoveFromSequenceButton
          "
          class="secondary_small gap-1 flex items-center justify-center"
          @click="handleBulkRemoveClick"
        >
          <ThemeImage
            :icon="imageNames.DISCONNECT_GREY"
            alt="remove icon"
            class="size-4"
          />
          Remove from {{ featureNames.SEQUENCES }}
        </a-button>

        <CustomDropdown
          v-else-if="
            filteredBulkActionMoreOptions?.length &&
            props.source !== entityTypes.SEQUENCE_ENTITY
          "
          maxHeight="310px"
          gap="10px"
          position="end"
          ref="moreDropdownRef"
        >
          <template #trigger>
            <a-button
              class="secondary_small gap-1 flex items-center justify-center"
            >
              <ThemeImage
                :icon="imageNames.MORE_VERTICAL"
                alt="more icon"
                class="size-4"
              />
              More
            </a-button>
          </template>

          <template #default>
            <div
              v-for="(item, index) in filteredBulkActionMoreOptions"
              :key="`${item.id}_bulk_action_${index}`"
              class="dropdown_item h-10 flex items-center gap-2 mr-10"
              @click.stop="item.action()"
            >
              <ThemeImage
                class="size-4"
                :icon="item.icon"
                :alt="`${item.id}_icon`"
              />
              <span>{{ item?.text || "" }}</span>
            </div>
          </template>
        </CustomDropdown>
      </div>

      <div
        :class="[
          'overflow-x-scroll custom_scrollbar',
          width === '100%' ? '' : showBorder ? 'table_wrapper' : '',
        ]"
      >
        <div class="custom_table" :style="customTableStyle">
          <div class="table_container" :style="`width: ${width}`">
            <div class="flex w-full">
              <div class="grid table_head" :style="getLeftGridStyle">
                <div
                  v-if="showCheckboxes"
                  class="col-span-1 table_header flex justify-center items-center border_right"
                >
                  <a-checkbox
                    v-if="showSelectAll"
                    :checked="isAllPageSelected || isAllPageSelectedEntity"
                    :indeterminate="isIndeterminate || isIndeterminateEntity"
                    @change="onSelectAllChange"
                  />
                </div>
                <div
                  v-for="(header, index) in leftHeaders"
                  :key="index"
                  :class="[
                    `col-span-${header.width}`,
                    { border_right: addSideBorder },
                    {
                      'cursor-pointer': showSort && header.key !== 'local_time',
                    },
                    {
                      'justify-center':
                        header.key === 'engagement' || header.key === 'sender',
                    },
                  ]"
                  class="table_header"
                  @click="handleHeaderClick(header)"
                >
                  <HeaderLabel :label="header.label" />
                  <div
                    v-if="showSort && header.key !== 'local_time'"
                    :class="[
                      Object.values(currentSort)?.some(
                        (item) => item.field === header.key
                      )
                        ? 'sorting_applied'
                        : '',
                      'flex flex-col gap-1',
                    ]"
                  >
                    <ThemeImage
                      class="w-[6px] h-1 cursor-pointer"
                      :icon="getSortIcon(header.key, 'asc')"
                      alt="sort_up_arrow"
                      @click.stop="toggleSort(header.key, 'asc')"
                    />
                    <ThemeImage
                      class="w-[6px] h-1 cursor-pointer rotate-180"
                      :icon="getSortIcon(header.key, 'desc')"
                      alt="sort_down_arrow"
                      @click.stop="toggleSort(header.key, 'desc')"
                    />
                  </div>
                </div>
              </div>

              <div
                v-if="showActionsColumn && actionPosition"
                class="action_column_header border_right"
                :style="{ minWidth: actionHeaderCSS }"
              >
                {{ quickActions?.length > 0 ? "Actions" : "" }}
              </div>

              <div
                v-if="rightHeaders.length > 0"
                class="grid table_head"
                :style="getRightGridStyle"
              >
                <div
                  v-for="(header, index) in rightHeaders"
                  :key="index"
                  :class="[
                    `col-span-${header.width}`,
                    { border_right: addSideBorder },
                    {
                      'cursor-pointer': showSort && header.key !== 'local_time',
                    },
                    {
                      'justify-center':
                        header.key === 'engagement' || header.key === 'sender',
                    },
                  ]"
                  class="table_header"
                  @click="handleHeaderClick(header)"
                >
                  <HeaderLabel :label="header.label" />
                  <div
                    v-if="showSort && header.key !== 'local_time'"
                    :class="[
                      Object.values(currentSort)?.some(
                        (item) => item.field === header.key
                      )
                        ? 'sorting_applied'
                        : '',
                      'flex flex-col gap-1',
                    ]"
                  >
                    <ThemeImage
                      class="w-[6px] h-1 cursor-pointer"
                      :icon="getSortIcon(header.key, 'asc')"
                      alt="sort_up_arrow"
                      @click.stop="toggleSort(header.key, 'asc')"
                    />
                    <ThemeImage
                      class="w-[6px] h-1 cursor-pointer rotate-180"
                      :icon="getSortIcon(header.key, 'desc')"
                      alt="sort_down_arrow"
                      @click.stop="toggleSort(header.key, 'desc')"
                    />
                  </div>
                </div>
              </div>

              <div
                v-if="showActionsColumn && !actionPosition"
                class="action_column_header"
                :style="{ minWidth: actionHeaderCSS }"
              >
                {{ quickActions?.length > 0 ? "Actions" : "" }}
              </div>
            </div>

            <div
              class="table_body"
              :style="{
                maxHeight: width === '100%' ? height : `calc(${height} - 10px)`,
              }"
            >
              <EditableRow
                v-for="(row, rowIndex) in data"
                :key="row.id || rowIndex"
                :row-data="row"
                :edit-config="editConfig"
                @save="handleRowSave"
                @cancel="handleRowCancel"
                @edit="handleRowEdit"
                v-if="editConfig && Object.keys(editConfig).length > 0"
              >
                <template #default="{ editing, formState }">
                  <div :class="[`flex w-full`]">
                    <div
                      class="grid table_row"
                      :class="{ 'cursor-pointer': customRow }"
                      :style="getLeftGridStyle"
                    >
                      <div
                        v-if="showCheckboxes"
                        class="col-span-1 flex justify-center items-center border_right"
                      >
                        <a-checkbox
                          :checked="isSelected(row) || isEntitySelected(row)"
                          :disabled="row.disabled"
                          @change="(e) => onSelectChange(e, row)"
                        />
                      </div>
                      <template v-if="customRow">
                        <a
                          :href="customRow(row)?.['data-href']"
                          class="contents"
                          @click.prevent
                        >
                          <TableCell
                            v-for="(header, colIndex) in leftHeaders"
                            :key="colIndex"
                            :header="header"
                            :row="row"
                            :col-index="colIndex"
                            :source="source"
                            :custom-column-render="customColumnRender"
                            :empty-cell-value="emptyCellValue"
                            :add-side-border="addSideBorder"
                            :custom-row="customRow"
                            :suffix-key="suffixKey"
                            :suffix-component="suffixComponent"
                            :description-key="descriptionKey"
                            :extra-data="extraData"
                            :edit-config="editConfig"
                            @update="$emit('update', $event)"
                            @refresh="$emit('refresh', $event)"
                          />
                        </a>
                      </template>
                      <template v-else>
                        <TableCell
                          v-for="(header, colIndex) in leftHeaders"
                          :key="colIndex"
                          :header="header"
                          :row="row"
                          :col-index="colIndex"
                          :source="source"
                          :custom-column-render="customColumnRender"
                          :empty-cell-value="emptyCellValue"
                          :add-side-border="addSideBorder"
                          :custom-row="customRow"
                          :suffix-key="suffixKey"
                          :suffix-component="suffixComponent"
                          :description-key="descriptionKey"
                          :extra-data="extraData"
                          :edit-config="editConfig"
                          @update="$emit('update', $event)"
                          @refresh="$emit('refresh', $event)"
                        />
                      </template>
                    </div>

                    <TableActions
                      v-if="showActionsColumn && actionPosition"
                      :row="row"
                      :row-index="rowIndex"
                      :actions="actions"
                      :quick-actions="quickActions"
                      :source="source"
                      :entity-lists="entityLists"
                      :sequences="sequences"
                      :modelValue="sequencesSearchQuery"
                      @update:modelValue="sequencesSearchQuery = $event"
                      :is-sequence-enabled="isSequenceEnabled"
                      :column-width="actionColumnCSS"
                      :is-last-column="false"
                      :dropdown-index="dropdownIndex"
                      :from-prospect="fromProspect"
                      :dropdown-position="dropdownPosition"
                      :custom-action-key="customActionKey"
                      :custom-action-component="customActionComponent"
                      @action="performAction"
                      :prospect-data="prospectData"
                      :pending-ids="pendingIds"
                      @entity-list-created="toAddEntityLists"
                      @entity-list-created-apollo="addApolloEntityToList"
                      @sequence-created-apollo="addApolloEntityToSequence"
                      @sequence-created="addPeopleToSequence"
                      @list-create="handleListCreateDropdown"
                      @sequence-create="handleSequenceCreated"
                      :is-more-options-disabled="isMoreOptionsDisabled"
                    />

                    <div
                      v-if="rightHeaders.length > 0"
                      class="grid table_row"
                      :class="{ 'cursor-pointer': customRow }"
                      :style="getRightGridStyle"
                    >
                      <TableCell
                        v-for="(header, colIndex) in rightHeaders"
                        :key="colIndex"
                        :header="header"
                        :row="row"
                        :col-index="colIndex"
                        :source="source"
                        :custom-column-render="customColumnRender"
                        :empty-cell-value="emptyCellValue"
                        :add-side-border="addSideBorder"
                        :custom-row="customRow"
                        :suffix-key="suffixKey"
                        :suffix-component="suffixComponent"
                        :description-key="descriptionKey"
                        :extra-data="extraData"
                        :edit-config="editConfig"
                        @update="$emit('update', $event)"
                        @refresh="$emit('refresh', $event)"
                      />
                    </div>

                    <TableActions
                      v-if="showActionsColumn && !actionPosition"
                      :row="row"
                      :row-index="rowIndex"
                      :actions="actions"
                      :quick-actions="quickActions"
                      :source="source"
                      :entity-lists="entityLists"
                      :sequences="sequences"
                      :modelValue="sequencesSearchQuery"
                      @update:modelValue="sequencesSearchQuery = $event"
                      :is-sequence-enabled="isSequenceEnabled"
                      :column-width="actionColumnCSS"
                      :is-last-column="true"
                      :from-prospect="fromProspect"
                      :dropdown-index="dropdownIndex"
                      :dropdown-position="dropdownPosition"
                      :custom-action-key="customActionKey"
                      :custom-action-component="customActionComponent"
                      @action="performAction"
                      :prospect-data="prospectData"
                      @entity-list-created="toAddEntityLists"
                      @sequence-created="addPeopleToSequence"
                      @entity-list-created-apollo="addApolloEntityToList"
                      @sequence-created-apollo="addApolloEntityToSequence"
                      @list-create="handleListCreateDropdown"
                      @sequence-create="handleSequenceCreated"
                      :is-more-options-disabled="isMoreOptionsDisabled"
                    />
                  </div>
                </template>
              </EditableRow>

              <div
                v-for="(row, rowIndex) in data"
                :key="rowIndex"
                :class="[`flex w-full`]"
                v-if="!editConfig || Object.keys(editConfig).length === 0"
              >
                <div
                  class="grid table_row"
                  :class="{ 'cursor-pointer': customRow }"
                  :style="getLeftGridStyle"
                >
                  <div
                    v-if="showCheckboxes"
                    class="col-span-1 flex justify-center items-center border_right"
                  >
                    <a-checkbox
                      :checked="isSelected(row) || isEntitySelected(row)"
                      :disabled="row.disabled"
                      @change="(e) => onSelectChange(e, row)"
                    />
                  </div>
                  <template v-if="customRow">
                    <a
                      :href="customRow(row)?.['data-href']"
                      class="contents"
                      @click.prevent
                    >
                      <TableCell
                        v-for="(header, colIndex) in leftHeaders"
                        :key="colIndex"
                        :header="header"
                        :row="row"
                        :col-index="colIndex"
                        :source="source"
                        :custom-column-render="customColumnRender"
                        :empty-cell-value="emptyCellValue"
                        :add-side-border="addSideBorder"
                        :custom-row="customRow"
                        :suffix-key="suffixKey"
                        :suffix-component="suffixComponent"
                        :description-key="descriptionKey"
                        :extra-data="extraData"
                        @update="$emit('update', $event)"
                        @refresh="$emit('refresh', $event)"
                      />
                    </a>
                  </template>
                  <template v-else>
                    <TableCell
                      v-for="(header, colIndex) in leftHeaders"
                      :key="colIndex"
                      :header="header"
                      :row="row"
                      :col-index="colIndex"
                      :source="source"
                      :custom-column-render="customColumnRender"
                      :empty-cell-value="emptyCellValue"
                      :add-side-border="addSideBorder"
                      :custom-row="customRow"
                      :suffix-key="suffixKey"
                      :suffix-component="suffixComponent"
                      :description-key="descriptionKey"
                      :extra-data="extraData"
                      @update="$emit('update', $event)"
                      @refresh="$emit('refresh', $event)"
                    />
                  </template>
                </div>

                <TableActions
                  v-if="showActionsColumn && actionPosition"
                  :row="row"
                  :row-index="rowIndex"
                  :actions="actions"
                  :quick-actions="quickActions"
                  :source="source"
                  :entity-lists="entityLists"
                  :sequences="sequences"
                  :modelValue="sequencesSearchQuery"
                  @update:modelValue="sequencesSearchQuery = $event"
                  :is-sequence-enabled="isSequenceEnabled"
                  :column-width="actionColumnCSS"
                  :is-last-column="false"
                  :dropdown-index="dropdownIndex"
                  :from-prospect="fromProspect"
                  :dropdown-position="dropdownPosition"
                  :custom-action-key="customActionKey"
                  :custom-action-component="customActionComponent"
                  @action="performAction"
                  :prospect-data="prospectData"
                  :pending-ids="pendingIds"
                  @entity-list-created="toAddEntityLists"
                  @entity-list-created-apollo="addApolloEntityToList"
                  @sequence-created-apollo="addApolloEntityToSequence"
                  @sequence-created="addPeopleToSequence"
                  @list-create="handleListCreateDropdown"
                  @sequence-create="handleSequenceCreated"
                  :is-more-options-disabled="isMoreOptionsDisabled"
                />

                <div
                  v-if="rightHeaders.length > 0"
                  class="grid table_row"
                  :class="{ 'cursor-pointer': customRow }"
                  :style="getRightGridStyle"
                >
                  <TableCell
                    v-for="(header, colIndex) in rightHeaders"
                    :key="colIndex"
                    :header="header"
                    :row="row"
                    :col-index="colIndex"
                    :source="source"
                    :custom-column-render="customColumnRender"
                    :empty-cell-value="emptyCellValue"
                    :add-side-border="addSideBorder"
                    :custom-row="customRow"
                    :suffix-key="suffixKey"
                    :suffix-component="suffixComponent"
                    :description-key="descriptionKey"
                    :extra-data="extraData"
                    @update="$emit('update', $event)"
                    @refresh="$emit('refresh', $event)"
                  />
                </div>

                <TableActions
                  v-if="showActionsColumn && !actionPosition"
                  :row="row"
                  :row-index="rowIndex"
                  :actions="actions"
                  :quick-actions="quickActions"
                  :source="source"
                  :entity-lists="entityLists"
                  :sequences="sequences"
                  :modelValue="sequencesSearchQuery"
                  @update:modelValue="sequencesSearchQuery = $event"
                  :is-sequence-enabled="isSequenceEnabled"
                  :column-width="actionColumnCSS"
                  :is-last-column="true"
                  :from-prospect="fromProspect"
                  :dropdown-index="dropdownIndex"
                  :dropdown-position="dropdownPosition"
                  :custom-action-key="customActionKey"
                  :custom-action-component="customActionComponent"
                  @action="performAction"
                  :prospect-data="prospectData"
                  @entity-list-created="toAddEntityLists"
                  @sequence-created="addPeopleToSequence"
                  @entity-list-created-apollo="addApolloEntityToList"
                  @sequence-created-apollo="addApolloEntityToSequence"
                  @list-create="handleListCreateDropdown"
                  @sequence-create="handleSequenceCreated"
                  :is-more-options-disabled="isMoreOptionsDisabled"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <PaginationHandler
      :pagination="pagination"
      :per-page-options="perPageOptions"
      :allow-change-page-size="showPageSize"
      @update:pagination="handlePaginationUpdate"
    />

    <CreateSequenceModal
      :width="'500px'"
      :title="`Create New ${featureNames.SEQUENCES}`"
      v-model:visible="showSequenceCreate"
      @sequence-created="handleAddToSequence"
      :add-action="true"
      :show-message="false"
      :navigate-after-creation="false"
    />

    <CustomModal
      v-model:visible="bulkDeleteModalVisible"
      title="Delete View"
      width="36vw"
    >
      <span
        >Are you sure you want to delete <b>{{ entityTextWithCount }}</b
        >?</span
      >
      <template #footer>
        <a-button
          class="primary_medium"
          :loading="bulkDeleting"
          @click="bulkDelete"
        >
          {{ bulkDeleting ? "Deleting.." : "Delete" }}
        </a-button>
      </template>
    </CustomModal>

    <BulkRemoveFromSequenceModal
      v-model:visible="bulkRemoveFromSequenceModalVisible"
      :people-text="peopleText"
      :loading-count="props.loadingRemovableCount"
      @confirm="handleConfirmBulkRemove"
    />

    <BulkExportModal
      :source="source"
      :title="`Export ${toTitleCase(entityText)}`"
      :select-count="selectedCount"
      :description="`${entityTextWithCount} will be exported to your email`"
      v-model:visible="bulkExportModalVisible"
      @confirm="bulkExport"
    />

    <ValidationModal
      v-if="selectedSequenceId"
      v-model:visible="sequenceAddition.state.modalStates.validationModal"
      :validation-results="sequenceAddition.state.validationResults"
      :sequence-name="selectedSequenceName"
      :sequence-id="selectedSequenceId"
      :validation-type="validationType"
      @add-to-journey="handleValidationAddToJourney"
      @update-validation-results="sequenceAddition.updateValidationResults"
    />

    <SetupSenderDetailsModal
      :pending-validation-data="
        sequenceAddition.state.pendingValidationData || null
      "
      v-model:visible="
        sequenceAddition.state.modalStates.setupSenderDetailsModal
      "
      :sequence-id="selectedSequenceId"
      :source="props.prospectData ? props.source : 'custom_table'"
      @save="handleSetupSenderDetailsSave"
    />
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  reactive,
} from "vue";
import { useRouter } from "vue-router";

import CustomDropdown from "@/src/components/CustomDropdown.vue";
import DashboardService from "@/src/api/DashboardService.js";
import { message } from "ant-design-vue";
import {
  formatEntityTypeText,
  toGetAllEntityLists,
  toTitleCase,
} from "@/src/utils/utils.js";
import PaginationHandler from "@/src/components/custom_table_components/PaginationHandler.vue";
import SequenceService from "@/src/api/SequenceService.js";
import CreateSequenceModal from "@/src/views/sequence/actions/CreateSequenceModal.vue";
import AddToListDropdown from "./AddToListDropdown.vue";
import AddToDialerCampaign from "./AddToDialerCampaign.vue";
import RemoveFromList from "./RemoveFromList.vue";
import AddToSequenceDropdown from "./AddToSequenceDropdown.vue";
import TableActions from "./TableActions.vue";
import TableCell from "./TableCell.vue";
import HeaderLabel from "@/src/components/custom_table_components/HeaderLabel.vue";
import AssignOwnerDropdown from "@/src/components/custom_table_components/AssignOwnerDropdown.vue";
import { debounce } from "lodash";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import EditableRow from "@/src/components/custom_table_components/EditableRow.vue";
import store from "@/src/store/index.js";
import BulkAssignNotes from "@/src/components/custom_table_components/BulkAssignNotes.vue";
import { entityBulkActionsMap } from "@/src/constants/tableConstants.js";
import CustomModal from "@/src/components/CustomModal.vue";
import BulkExportModal from "@/src/components/custom_table_components/BulkExportModal.vue";
import BulkAssignTasks from "@/src/components/custom_table_components/BulkAssignTasks.vue";
import ValidationModal from "@/src/views/sequence/ValidationModal.vue";
import SetupSenderDetailsModal from "@/src/components/sequences_components/SetupSenderDetailsModal.vue";
import BulkRemoveFromSequenceModal from "@/src/components/sequences_components/BulkRemoveFromSequenceModal.vue";
import { useSequenceAddition } from "@/src/composables/useSequenceAddition.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import eventBus from "@/src/utils/eventBus.js";
import { showAlreadyExistsMessage, checkIsEnabled } from "@/src/utils/utils.js";
import { handleApiError } from "@/src/utils/errorUtils.js";
import { featureNames } from "@/src/constants/globalConstants";

const props = defineProps({
  source: { type: String, default: "" },
  headers: Object,
  data: Object,
  pagination: Object,
  showActionsColumn: { type: Boolean, default: false },
  showSelectionInfo: { type: Boolean, default: true },
  actions: { type: Array, default: () => [] },
  quickActions: { type: Array, default: () => [], required: false },
  customRow: { type: Function },
  width: { type: String, default: "100%" },
  height: { type: String, default: "72.8vh" },
  addSideBorder: { type: Boolean, default: false },
  customColumnRender: { type: Object, default: () => ({}) },
  initialSort: { type: Object, default: () => ({}) },
  showSort: { type: Boolean, default: false },
  toShowActions: { type: [Function, Boolean], default: false },
  prefixImageBorderRadius: { type: String, default: "50%" },
  showCheckboxes: { type: Boolean, default: false },
  showSelectAll: { type: Boolean, default: true },
  isMoreOptionsDisabled: { type: String, required: false },
  selection: {
    type: Object,
    default: () => ({
      selected_ids: [],
      unselected_ids: [],
      unselected_emails: [],
      select_all: false,
    }),
  },
  selectedEntities: {
    type: Object,
    default: () => ({
      selected_entities: [],
      unselected_entities: [],
      select_all: false,
    }),
  },
  identifierKey: { type: String, default: "id" },
  tableType: { type: String, default: "" },
  listId: { type: Number, required: false },
  suffixKey: { type: Object, required: false },
  suffixComponent: { type: Object, required: false },
  descriptionKey: { type: Object, required: false },
  totalColumns: { type: Number, default: 12 },
  showPageSize: { type: Boolean, default: true },
  emptyCellValue: { type: String, default: "" },
  viewId: { type: Number, default: null },
  actionPosition: { type: Number, default: null },
  extraData: { type: Object, required: false, default: null },
  customActionKey: { type: String, required: false },
  fromProspect: { type: Boolean, required: false, default: false },
  customActionComponent: { type: Object, default: null },
  prospectData: { type: Boolean, default: false, required: false },
  customActionWidth: { type: Number, default: 52 },
  perPageOptions: { type: Object, required: false },
  editConfig: { type: Object, default: () => ({}) },
  addConfig: { type: Object, default: () => ({}) },
  showBorder: { type: Boolean, default: true },
  borderRadius: { type: String, default: "8px" },
  emailFilterData: { type: Object, default: () => ({}) },
  sequenceId: { type: [String, Number], required: false },
  loadingRemovableCount: { type: Boolean, default: false },
  showRemoveFromSequenceButton: { type: Boolean, default: false },
  allSelectedEntitiesRemoved: { type: Boolean, default: false },
  handleBulkRemoveFromSequence: { type: Function, default: null },
  confirmBulkRemoveFromSequence: { type: Function, default: null },
  getRemovableCount: { type: Function, default: null },
});

const emit = defineEmits([
  "update:pagination",
  "update:sort",
  "update:selection",
  "update:selected-entities",
  "update:listRemove",
  "update:owner-assigned",
  "update",
  "refresh",
  "row-save",
  "row-add",
  "row-cancel",
  "row-edit",
  "data-update",
  "entity-list-created",
  "sequence-created",
  "update:sequence-entity-tracker",
]);

const isAsyncBulkEnrichmentEnabled = ref(false);
const loadAsyncBulkEnrichmentFlag = async () => {
  try {
    isAsyncBulkEnrichmentEnabled.value = await checkIsEnabled(
      "async_waterfall_bulk_enrichment_enabled"
    );
  } catch (error) {
    console.error(
      "Failed to load feature flag async_waterfall_bulk_enrichment:",
      error
    );
  }
};

const sequenceAddition = useSequenceAddition();
const router = useRouter();

const moreDropdownRef = ref(null);
const sequenceDropdown = ref(null);
const bulkDeleteModalVisible = ref(false);
const bulkExportModalVisible = ref(false);
const bulkRemoveFromSequenceModalVisible = ref(false);
const selectedPerPage = ref(props.pagination?.pageSize || 20);
const currentSort = ref(props.initialSort || {});
const dropdownIndex = ref(null);
const dropdownPosition = ref({ top: 0, left: 0 });
const entityLists = ref(null);
const sequences = ref(null);
const sequencesSearchQuery = ref("");
const showEntityCreate = ref(false);
const showSequenceCreate = ref(false);
const isSequenceEnabled = ref(true);
const rowContext = ref(null);
const rowId = ref(null);
const pendingIds = ref([]);
const hasListAccess = computed(() => store.getters["rbac/hasListsAccess"]);
const hasSequenceAccess = computed(
  () => store.getters["rbac/hasSequencesAccess"]
);
const campaignDetails = computed(
  () => store.getters["callsCampaign/campaignDetails"]
);
const selectedSequenceName = ref("");
const selectedSequenceId = ref(null);
const validationType = ref(null);
const isNewlyCreatedSequence = ref(false);
const bulkDeleting = ref(false);

const bulkActionMoreOptions = [
  {
    id: "enrich_email",
    text: "Enrich Email",
    icon: imageNames.AGI_PURPLE,
    action: () =>
      handleBulkEnrichEmail({
        selection: props.selection,
        source: props.source,
      }),
  },
  {
    id: "enrich_phone_number",
    text: "Enrich Phone Number",
    icon: imageNames.AGI_PURPLE,
    action: () =>
      handleBulkEnrichPhoneNumber({
        selection: props.selection,
        source: props.source,
      }),
  },
  {
    id: "enrich_company",
    text: "Enrich Company",
    icon: imageNames.AGI_PURPLE,
    action: () => handleBulkEnrichCompanies(props.selection),
  },
  {
    id: "run_market_research",
    text: "Run Market Research",
    icon: imageNames.AGI_PURPLE,
    action: () =>
      handleBulkMarketResearch({
        selection: props.selection,
        source: props.source,
      }),
  },
  {
    id: "export",
    text: "Export",
    icon: imageNames.EXPORT_GREY,
    action: () => handleBulkExportClick(),
  },
  {
    id: "delete",
    text: "Delete",
    icon: imageNames.DELETE_GREY,
    action: () => handleBulkDeleteClick(),
  },
  {
    id: "remove_from_sequence",
    text: `Remove from ${featureNames.SEQUENCES}`,
    icon: imageNames.DELETE_GREY,
    action: () => handleBulkRemoveClick(),
  },
];

const showAddToListDropdown = computed(
  () =>
    [
      entityTypes.CONTACT,
      entityTypes.COMPANY,
      entityTypes.LEAD,
      entityTypes.EMAIL,
      entityTypes.SEQUENCE_EMAILS,
    ].includes(props.source) && hasListAccess.value
);

const showAddNotesDropdown = computed(() =>
  [entityTypes.CONTACT, entityTypes.COMPANY, entityTypes.LEAD].includes(
    props.source
  )
);

const showSelection = computed(
  () =>
    props.selection?.selected_ids?.length > 0 &&
    props.selection?.unselected_ids?.length !== props.pagination?.total &&
    props.showSelectionInfo &&
    props.source !== entityTypes.RECOMMENDED_PROMPTS &&
    props.source !== entityTypes.SETUP_SENDER_DETAILS
);

const showAddTasksDropdown = computed(() =>
  [
    entityTypes.CONTACT,
    entityTypes.COMPANY,
    entityTypes.LEAD,
    entityTypes.DEAL,
  ].includes(props.source)
);

const showAddToDialerCampaign = computed(
  () =>
    [entityTypes.CAMPAIGN].includes(props.source) && !props.selection.select_all
);

const peopleText = computed(() => {
  if (props.source === entityTypes.SEQUENCE_ENTITY) {
    const count = props.getRemovableCount ? props.getRemovableCount() : 0;
    return count === 1 ? `${count} person` : `${count} people`;
  }

  const count = selectedCount.value;
  return count === 1 ? `${count} person` : `${count} people`;
});

const filteredBulkActionMoreOptions = computed(() => {
  const applicableActionIds =
    entityBulkActionsMap?.[props.source] || entityBulkActionsMap["default"];

  return bulkActionMoreOptions.filter((item) => {
    // Filter by entity type
    if (!applicableActionIds.includes(item.id)) {
      return false;
    }

    // Filter delete action if select_all is true
    if (item.id === "delete" && props.selection.select_all) {
      return false;
    }

    // Filter remove_from_sequence if all entities are removed
    if (
      item.id === "remove_from_sequence" &&
      props.allSelectedEntitiesRemoved
    ) {
      return false;
    }

    // Filter export action based on permissions
    if (item.id === "export") {
      let resourceSlug = null;

      if (props.source === entityTypes.CONTACT) {
        resourceSlug = "contacts";
      } else if (props.source === entityTypes.LEAD) {
        resourceSlug = "leads";
      } else if (props.source === entityTypes.COMPANY) {
        resourceSlug = "companies";
      }

      if (resourceSlug) {
        return store.getters["rbac/canExport"](resourceSlug);
      }

      return store.getters["rbac/hasExportAccess"];
    }

    // Filter delete action based on bulk delete permissions
    if (item.id === "delete") {
      let resourceSlug = null;

      if (props.source === entityTypes.CONTACT) {
        resourceSlug = "contacts";
      } else if (props.source === entityTypes.LEAD) {
        resourceSlug = "leads";
      } else if (props.source === entityTypes.COMPANY) {
        resourceSlug = "companies";
      }

      if (resourceSlug) {
        return store.getters["rbac/canBulkDelete"](resourceSlug);
      }

      return store.getters["rbac/hasBulkDeleteAccess"];
    }

    return true;
  });
});

const entityText = computed(() =>
  formatEntityTypeText({
    entityType: props.source,
    count: selectedCount.value,
    includeCount: false,
  })
);

const entityTextWithCount = computed(() =>
  formatEntityTypeText({
    entityType: props.source,
    count: selectedCount.value,
  })
);

const leftHeaders = computed(() => {
  if (!props.actionPosition) return props.headers;
  return props.headers.slice(0, props.actionPosition);
});

const rightHeaders = computed(() => {
  if (!props.actionPosition) return [];
  return props.headers.slice(props.actionPosition);
});

const leftGridWidth = computed(() => {
  if (!props.actionPosition) return props.width;
  const totalLeftColumns = leftHeaders.value.reduce(
    (acc, header) => acc + header.width,
    0
  );
  const totalColumns = props.headers.reduce(
    (acc, header) => acc + header.width,
    0
  );
  return `${(totalLeftColumns / totalColumns) * 100}%`;
});

const rightGridWidth = computed(() => {
  if (!props.actionPosition) return "0";
  const totalRightColumns = rightHeaders.value.reduce(
    (acc, header) => acc + header.width,
    0
  );
  const totalColumns = props.headers.reduce(
    (acc, header) => acc + header.width,
    0
  );
  return `${(totalRightColumns / totalColumns) * 100}%`;
});

const getLeftGridStyle = computed(() => {
  const totalCols = leftHeaders.value.reduce(
    (acc, header) => acc + header.width,
    0
  );

  const checkboxColumn = props.showCheckboxes ? "40px " : "";
  const gridColumns = `repeat(${totalCols}, minmax(0, 1fr))`;

  return {
    gridTemplateColumns: `${checkboxColumn}${gridColumns}`,
    width: leftGridWidth.value,
  };
});

const getRightGridStyle = computed(() => {
  const totalCols = rightHeaders.value.reduce(
    (acc, header) => acc + header.width,
    0
  );

  return {
    gridTemplateColumns: `repeat(${totalCols}, minmax(0, 1fr))`,
    width: rightGridWidth.value,
  };
});

const customTableStyle = computed(() => {
  if (String(props.width).trim() === "100%") {
    return {
      ...(props.showBorder && { border: "1px solid var(--white-opacity-12)" }),
      borderRadius: props.borderRadius,
    };
  } else {
    return {
      borderTopRightRadius: props.borderRadius,
      borderTopLeftRadius: props.borderRadius,
    };
  }
});

const actionColumnCSS = computed(() => {
  const customActionComponentWidth = props.customActionComponent
    ? props.customActionWidth
    : 0;
  const quickActionWidth = 32;
  const moreActionWidth = 32;
  const padding = 16;
  const gap = 4;

  const totalQuickActionsWidth =
    props.quickActions?.length * (quickActionWidth + gap);
  const moreActionsWidth = props.actions?.length > 0 ? moreActionWidth : 0;

  const totalWidth =
    totalQuickActionsWidth +
    moreActionsWidth +
    customActionComponentWidth +
    padding;
  return `${totalWidth}px`;
});

const actionHeaderCSS = computed(() => {
  return actionColumnCSS.value;
});

const showAddToSequenceDropdown = computed(() => {
  if (!isSequenceEnabled.value || !hasSequenceAccess.value) {
    return false;
  }

  if (
    props.source === entityTypes.EMAIL ||
    props.source === entityTypes.SEQUENCE_EMAILS ||
    props.source === entityTypes.LINKEDIN_LOG ||
    props.source === entityTypes.SEQUENCE_ENTITY
  ) {
    return true;
  }

  return (
    [entityTypes.CONTACT, entityTypes.LEAD, entityTypes.CAMPAIGN].includes(
      props.source
    ) && !props.selection.select_all
  );
});

const showAssignOwnerDropdown = computed(
  () =>
    ![
      entityTypes.EMAIL,
      entityTypes.SEQUENCE_EMAILS,
      entityTypes.CAMPAIGN,
      entityTypes.CAMPAIGNS_IN_ENGAGE,
      entityTypes.SEQUENCE_ENTITY,
      entityTypes.LINKEDIN_LOG,
      entityTypes.SEQUENCE_LINKEDIN_LOGS,
    ].includes(props.source)
);

const showEmailExport = computed(
  () =>
    props.source === entityTypes.EMAIL ||
    props.source === entityTypes.SEQUENCE_EMAILS ||
    props.source === entityTypes.LINKEDIN_LOG ||
    props.source === entityTypes.SEQUENCE_LINKEDIN_LOGS
);

const isAllPageSelected = computed(() => {
  return props.data.every((row) => isSelected(row));
});

const isIndeterminate = computed(() => {
  const selectedCount = props.data.filter((row) => isSelected(row)).length;
  return selectedCount > 0 && selectedCount < props.data.length;
});

const selectAllEnabled = computed(() => {
  return !!localStorage.getItem("select_all");
});

const handlePaginationUpdate = (newPagination) => {
  emit("update:pagination", newPagination);
};

const performAction = ({ action, row }) => {
  if (action.handler && typeof action.handler === "function") {
    action.handler(row);
  }
};

const handleBulkDeleteClick = () => {
  bulkDeleteModalVisible.value = true;
};

const handleBulkExportClick = () => {
  bulkExportModalVisible.value = true;
};

const handleBulkRemoveClick = async () => {
  if (!props.handleBulkRemoveFromSequence) {
    console.error("handleBulkRemoveFromSequence function not provided");
    return;
  }

  try {
    const shouldShowModal = await props.handleBulkRemoveFromSequence();
    if (shouldShowModal) {
      bulkRemoveFromSequenceModalVisible.value = true;
    }
  } catch (error) {
    console.error("Error in handleBulkRemoveClick:", error);
    message.error("An error occurred while preparing bulk removal");
  }
};

const handleConfirmBulkRemove = async () => {
  if (!props.confirmBulkRemoveFromSequence) {
    console.error("confirmBulkRemoveFromSequence function not provided");
    bulkRemoveFromSequenceModalVisible.value = false;
    return;
  }

  try {
    const success = await props.confirmBulkRemoveFromSequence();
    if (success) {
      bulkRemoveFromSequenceModalVisible.value = false;
    }
  } catch (error) {
    console.error("Error in handleConfirmBulkRemove:", error);
    message.error("An error occurred during bulk removal");
    bulkRemoveFromSequenceModalVisible.value = false;
  }
};

const handleEmailExport = () => {
  if (props.source === entityTypes.SEQUENCE_EMAILS) {
    eventBus.emit("sequence-emails/export", props.selection);
  } else if (props.source === entityTypes.LINKEDIN_LOG) {
    eventBus.emit("linkedin_logs/export", props.selection);
  } else if (props.source === entityTypes.SEQUENCE_LINKEDIN_LOGS) {
    eventBus.emit("sequence-linkedin-logs/export", props.selection);
  } else {
    eventBus.emit("emails/export", props.selection);
  }
};

const handleSequenceCreated = (row = null) => {
  if (row) {
    rowContext.value = row;
  }
  isNewlyCreatedSequence.value = false;
  showSequenceCreate.value = true;
  sequenceDropdown.value?.closeDropdown();
};

const handleListCreateDropdown = (row = null) => {
  if (row) {
    rowContext.value = row;
  }
  showEntityCreate.value = true;
};

const isSelected = (item) => {
  const id = item[props.identifierKey];
  if (props.selection.select_all) {
    return !props.selection.unselected_ids.includes(id);
  }
  return props.selection.selected_ids.includes(id);
};

const isEntitySelected = (item) => {
  const entity = {
    id: item[props.identifierKey],
    entity_type: item.entity_type,
  };

  const serializeEntity = (entity) => `${entity.id}:${entity.entity_type}`;
  const serializedEntity = serializeEntity(entity);

  if (props.selectedEntities.select_all) {
    return !props.selectedEntities.unselected_entities
      .map(serializeEntity)
      .includes(serializedEntity);
  }
  return props.selectedEntities.selected_entities
    .map(serializeEntity)
    .includes(serializedEntity);
};

const isAllPageSelectedEntity = computed(() => {
  return props.data.every((row) => isEntitySelected(row));
});

const isIndeterminateEntity = computed(() => {
  const selectedCount = props.data.filter((row) =>
    isEntitySelected(row)
  ).length;
  return selectedCount > 0 && selectedCount < props.data.length;
});

const selectedCount = computed(() => {
  if (props.selection.select_all) {
    return props.pagination.total - props.selection.unselected_ids.length;
  }
  return props.selection.selected_ids.length;
});

const onSelectAllChange = (e) => {
  const ids = props.data.map((row) => row[props.identifierKey]);
  updateSelection(ids, e.target.checked);

  const entities = props.data.map((row) => ({
    id: row[props.identifierKey],
    entity_type: row.entity_type,
  }));
  updateEntitySelection(entities, e.target.checked);
};

const onSelectChange = (e, item) => {
  updateSelection([item[props.identifierKey]], e.target.checked);
  const entity = {
    id: item[props.identifierKey],
    entity_type: item.entity_type,
  };
  updateEntitySelection([entity], e.target.checked);
};

const selectThisPage = () => {
  updateSelection(
    props.data.map((row) => row[props.identifierKey]),
    true
  );
  const entities = props.data.map((row) => ({
    id: row[props.identifierKey],
    entity_type: row.entity_type,
  }));
  updateEntitySelection(entities, true);
};

const selectAll = () => {
  emit("update:selection", {
    ...props.selection,
    select_all: true,
    unselected_ids: [],
    unselected_emails: [],
  });
  emit("update:selected-entities", {
    ...props.selectedEntities,
    select_all: true,
    unselected_entities: [],
  });
};

const unselectAll = () => {
  emit("update:selection", {
    selected_ids: [],
    unselected_ids: [],
    unselected_emails: [],
    select_all: false,
  });
  emit("update:selected-entities", {
    selected_entities: [],
    unselected_entities: [],
    select_all: false,
  });

  if (props.source === entityTypes.SEQUENCE_ENTITY) {
    emit("update:sequence-entity-tracker");
  }
};

const updateSelection = (ids, isSelected) => {
  let newSelection;

  let emailAddresses = [];
  if (
    props.source === entityTypes.EMAIL ||
    props.source === entityTypes.SEQUENCE_EMAILS
  ) {
    emailAddresses = ids
      .map((id) => {
        const email = props?.data?.find(
          (row) => row[props?.identifierKey] === id
        );
        return email?.sent_to?.email;
      })
      .filter(Boolean);
  }

  if (props.selection.select_all) {
    if (isSelected) {
      newSelection = {
        ...props.selection,
        unselected_ids: props.selection.unselected_ids.filter(
          (id) => !ids.includes(id)
        ),
      };

      if (
        (props.source === entityTypes.EMAIL ||
          props.source === entityTypes.SEQUENCE_EMAILS) &&
        emailAddresses.length > 0
      ) {
        newSelection.unselected_emails = (
          props.selection.unselected_emails || []
        ).filter((email) => !emailAddresses.includes(email));
      }
    } else {
      newSelection = {
        ...props.selection,
        unselected_ids: [
          ...new Set([...props.selection.unselected_ids, ...ids]),
        ],
      };

      if (
        (props.source === entityTypes.EMAIL ||
          props.source === entityTypes.SEQUENCE_EMAILS) &&
        emailAddresses.length > 0
      ) {
        newSelection.unselected_emails = [
          ...new Set([
            ...(props.selection?.unselected_emails || []),
            ...emailAddresses,
          ]),
        ];
      }
    }
  } else {
    if (isSelected) {
      newSelection = {
        ...props.selection,
        selected_ids: [...new Set([...props.selection.selected_ids, ...ids])],
      };
    } else {
      newSelection = {
        ...props.selection,
        selected_ids: props.selection.selected_ids.filter(
          (id) => !ids.includes(id)
        ),
      };
    }
  }
  emit("update:selection", newSelection);
};

const updateEntitySelection = (entities, isSelected) => {
  const serializeEntity = (entity) => `${entity.id}:${entity.entity_type}`;

  let newSelection;

  if (props.selectedEntities.select_all) {
    if (isSelected) {
      const unselectedMap = new Map(
        props.selectedEntities.unselected_entities.map((entity) => [
          serializeEntity(entity),
          entity,
        ])
      );
      entities.forEach((entity) => {
        unselectedMap.delete(serializeEntity(entity));
      });

      newSelection = {
        ...props.selectedEntities,
        unselected_entities: Array.from(unselectedMap.values()),
      };
    } else {
      const unselectedMap = new Map(
        [...props.selectedEntities.unselected_entities, ...entities].map(
          (entity) => [serializeEntity(entity), entity]
        )
      );
      newSelection = {
        ...props.selectedEntities,
        unselected_entities: Array.from(unselectedMap.values()),
      };
    }
  } else {
    if (isSelected) {
      const selectedMap = new Map(
        [...props.selectedEntities.selected_entities, ...entities].map(
          (entity) => [serializeEntity(entity), entity]
        )
      );
      newSelection = {
        ...props.selectedEntities,
        selected_entities: Array.from(selectedMap.values()),
      };
    } else {
      const entitiesToRemove = new Set(entities.map(serializeEntity));
      newSelection = {
        ...props.selectedEntities,
        selected_entities: props.selectedEntities.selected_entities.filter(
          (entity) => !entitiesToRemove.has(serializeEntity(entity))
        ),
      };
    }
  }
  emit("update:selected-entities", newSelection);
};

function notifySuccess(msg) {
  message.success(msg);
  unselectAll();
}
function notifyError(msg) {
  message.error(msg);
}

const toggleSort = (field, order) => {
  const newSort = { 1: { field, order } };
  currentSort.value = newSort;
  emit("update:sort", newSort);
};

const handleHeaderClick = (header) => {
  if (!props.showSort || header.key === "local_time") return;
  const sort = Object.values(currentSort.value).find(
    (item) => item.field === header.key
  );
  if (sort) {
    if (sort.order === "desc") {
      toggleSort(sort.field, "asc");
    } else {
      const newSort = {};
      currentSort.value = newSort;
      emit("update:sort", newSort);
    }
  } else {
    toggleSort(header.key, "desc");
  }
};

const handleClickOutside = (event) => {
  if (!event.target.closest(".action_wrapper")) {
    dropdownIndex.value = null;
  }
};

const getSortIcon = (field, order) => {
  return Object.values(currentSort.value)?.some(
    (item) =>
      item.field === field && item.field === field && item.order === order
  )
    ? imageNames.SORTING_ARROW_SELECTED
    : imageNames.SORTING_ARROW_GREY;
};

const getEntityList = async (entityType = null) => {
  if (
    props.source === entityTypes.EMAIL ||
    props.source === entityTypes.SEQUENCE_EMAILS ||
    props.source === entityTypes.LINKEDIN_LOG ||
    props.source === entityTypes.SEQUENCE_LINKEDIN_LOGS
  ) {
    const [contactLists, leadLists] = await Promise.all([
      toGetAllEntityLists("contact"),
      toGetAllEntityLists("lead"),
    ]);
    entityLists.value = [...(contactLists || []), ...(leadLists || [])];
  } else {
    entityLists.value = await toGetAllEntityLists(
      props.source === "employees" ? entityType.toLowerCase() : props.source
    );
  }
};

const fetchSequences = async (searchQuery = "") => {
  try {
    const response = await SequenceService.fetchSequences(0, 20, searchQuery);

    if (response?.success) {
      sequences.value = response?.sequences || [];
    } else {
      console.error("Failed to fetch sequences:", response.errors);
    }
  } catch (error) {
    console.error("Error fetching sequences:", error);
  }
};

const debouncedFetchSequences = debounce(fetchSequences, 300);

const handleAssignOwner = (assignmentData) => {
  emit("update:owner-assigned", assignmentData);
  handleClearEntityState();
};

const handleClearEntityState = () => {
  showEntityCreate.value = false;
  unselectAll();
};

const handleAddToSequence = ({ sequenceId }) => {
  showSequenceCreate.value = false;
  isNewlyCreatedSequence.value = true;

  if (props.prospectData) {
    selectedSequenceId.value = sequenceId;
    selectedSequenceName.value =
      sequences.value?.find((seq) => seq.id === sequenceId)?.name || "";
    rowId.value = rowContext?.value?.id;
    sequenceAddition.setDirectValidationData(1, "DIRECT");

    sequenceAddition.setModalState("setupSenderDetailsModal", true);
  } else {
    addPeopleToSequence({
      sequenceId: sequenceId,
      rowId: rowContext?.value?.id,
    });
  }
};

const handleAddToList = ({ listId }) => {
  if (props.prospectData) {
    addApolloEntityToList({ listId: listId, row: rowContext.value.id });
  } else {
    toAddEntityLists({ listId: listId, rowId: rowContext.value.id });
  }
};
const toAddEntityLists = async ({ listId, rowId = null }) => {
  if (
    props.source === entityTypes.EMAIL ||
    props.source === entityTypes.SEQUENCE_EMAILS ||
    props.source === entityTypes.LINKEDIN_LOG
  ) {
    const selectedList = entityLists.value?.find((list) => list.id === listId);
    emit("entity-list-created", {
      listId,
      rowId,
      listEntityType: selectedList?.entity_type,
    });
    return;
  }

  try {
    let payload;

    if (rowContext.value && !rowId) {
      rowId = rowContext.value;
    }

    if (rowId) {
      payload = {
        current_list_id: props.listId,
        list_id: listId,
        selected_ids: [rowId],
        unselected_ids: [],
        select_all: false,
        view_id: props.viewId,
      };
    } else {
      payload = {
        current_list_id: props.listId,
        list_id: listId,
        selected_ids: props.selection.selected_ids,
        unselected_ids: props.selection.unselected_ids,
        select_all: props.selection.select_all,
        view_id: props.viewId,
      };
    }

    const response = await DashboardService.addEntityLists(payload);
    if (response?.success) {
      message.success("Items added to the list");
    }
  } catch (error) {
    message.error("An error occurred while adding items to the list");
    console.error("Error while updating the entity list: ", error);
  } finally {
    await getEntityList();
    handleClearEntityState();
    rowContext.value = null;
  }
};

const toRemoveEntityListItems = async () => {
  try {
    const payload = {
      list_id: props.listId,
      selected_ids: props.selection.selected_ids,
      unselected_ids: props.selection.unselected_ids,
      select_all: props.selection.select_all,
    };

    if (props.viewId) {
      payload.view_id = props.viewId;
    }

    const response = await DashboardService.removeEntityListItems(payload);
    if (response?.success) {
      emit("update:listRemove");
    }
  } catch (error) {
    console.error("Error while removing entity list items: ", error);
  } finally {
    handleClearEntityState();
  }
};

const addPeopleToSequence = async ({ sequenceId, rowId = null }) => {
  selectedSequenceId.value = sequenceId;
  selectedSequenceName.value =
    sequences.value?.find((seq) => seq.id === sequenceId)?.name || "";

  if (
    props.source === entityTypes.EMAIL ||
    props.source === entityTypes.SEQUENCE_EMAILS
  ) {
    try {
      let entityEmails = [];

      if (rowContext.value && !rowId) {
        rowId = rowContext.value;
      }

      if (rowId) {
        const emailRow = props.data.find(
          (row) => row[props.identifierKey] === rowId
        );
        if (emailRow?.sent_to?.email) {
          entityEmails = [emailRow.sent_to.email];
        }
      } else if (props.selection.select_all) {
        await sequenceAddition.addEmailWithValidation(
          sequenceId,
          [],
          {
            ...props.emailFilterData,
            select_all: true,
            unselected_emails: props.selection.unselected_emails || [],
          }
        );
        sequenceDropdown.value?.closeDropdown();
        return;
      } else {
        entityEmails = props.selection.selected_ids
          .map((id) => {
            const email = props.data.find(
              (row) => row[props.identifierKey] === id
            );
            return email?.sent_to?.email;
          })
          .filter(Boolean);
      }

      if (entityEmails.length === 0) {
        message.error(
          `No valid email addresses found to add to ${featureNames.SEQUENCES}`
        );
        return;
      }

      await sequenceAddition.addEmailWithValidation(
        sequenceId,
        entityEmails,
        props.emailFilterData
      );

      sequenceDropdown.value?.closeDropdown();
    } catch (error) {
      message.error(
        `An error occurred while preparing email ${featureNames.SEQUENCES.toLowerCase()} setup`
      );
      console.error("Error while preparing email sequence setup: ", error);
    }
    return;
  }

  if (props.source === entityTypes.LINKEDIN_LOG) {
    try {
      let entityList = [];

      if (rowContext.value && !rowId) {
        rowId = rowContext.value;
      }

      if (rowId) {
        const linkedinLogRow = props.data.find(
          (row) => row[props.identifierKey] === rowId
        );
        if (linkedinLogRow) {
          if (linkedinLogRow.contact_id) {
            entityList = [
              { id: linkedinLogRow.contact_id, entity_type: "contact" },
            ];
          } else if (linkedinLogRow.lead_id) {
            entityList = [{ id: linkedinLogRow.lead_id, entity_type: "lead" }];
          }
        }
      } else {
        entityList = props.selection.selected_ids
          .map((id) => {
            const linkedinLog = props.data.find(
              (row) => row[props.identifierKey] === id
            );
            if (linkedinLog?.contact_id) {
              return { id: linkedinLog.contact_id, entity_type: "contact" };
            } else if (linkedinLog?.lead_id) {
              return { id: linkedinLog.lead_id, entity_type: "lead" };
            }
            return null;
          })
          .filter(Boolean);
      }

      if (entityList.length === 0) {
        message.error(
          `No valid contacts or leads found to add to ${featureNames.SEQUENCES}`
        );
        return;
      }

      await sequenceAddition.addManualWithValidation(sequenceId, entityList);

      sequenceDropdown.value?.closeDropdown();
    } catch (error) {
      message.error(
        `An error occurred while preparing LinkedIn log ${featureNames.SEQUENCES.toLowerCase()} setup`
      );
      console.error(
        "Error while preparing LinkedIn log sequence setup: ",
        error
      );
    }
    return;
  }

  if (props.source === entityTypes.SEQUENCE_ENTITY) {
    try {
      let entityList = [];

      if (rowContext.value && !rowId) {
        rowId = rowContext.value;
      }

      if (rowId) {
        const sequenceEntityRow = props.data.find(
          (row) => row[props.identifierKey] === rowId
        );
        if (
          sequenceEntityRow &&
          sequenceEntityRow.entity_id &&
          sequenceEntityRow.entity_type
        ) {
          entityList = [
            {
              id: sequenceEntityRow.entity_id,
              entity_type: sequenceEntityRow.entity_type.toLowerCase(),
            },
          ];
        }
      } else {
        entityList = props.selection.selected_ids
          .map((id) => {
            const sequenceEntity = props.data.find(
              (row) => row[props.identifierKey] === id
            );
            if (sequenceEntity?.entity_id && sequenceEntity?.entity_type) {
              return {
                id: sequenceEntity.entity_id,
                entity_type: sequenceEntity.entity_type.toLowerCase(),
              };
            }
            return null;
          })
          .filter(Boolean);
      }

      if (entityList.length === 0) {
        message.error(
          `No valid contacts or leads found to add to ${featureNames.SEQUENCES}`
        );
        return;
      }

      await sequenceAddition.addManualWithValidation(sequenceId, entityList);

      sequenceDropdown.value?.closeDropdown();
    } catch (error) {
      message.error(
        `An error occurred while preparing ${featureNames.SEQUENCES.toLowerCase()} setup`
      );
      console.error(
        "Error while preparing sequence entity sequence setup: ",
        error
      );
    }
    return;
  }

  try {
    let entityIds = [];
    let entityType = props.source.toLowerCase();

    if (props.source === entityTypes.CAMPAIGN) {
      entityType =
        campaignDetails.value?.entity_type?.toLowerCase() || "contact";
    }

    if (rowContext.value && !rowId) {
      rowId = rowContext.value;
    }

    if (rowId) {
      entityIds = [rowId];
    } else {
      entityIds = props.selection.selected_ids;
    }

    if (entityIds.length === 0) {
      message.error(
        `Please select entities to add to ${featureNames.SEQUENCES}`
      );
      return;
    }

    const entityList = entityIds.map((id) => ({
      id: id,
      entity_type: entityType,
    }));

    await sequenceAddition.addManualWithValidation(sequenceId, entityList);

    sequenceDropdown.value?.closeDropdown();
  } catch (error) {
    message.error("An error occurred while preparing sequence setup");
    console.error("Error while preparing sequence setup: ", error);
  }
};

const addApolloEntityToSequence = async ({
  sequenceId,
  rowId: entityRowId = null,
  configId = null,
}) => {
  try {
    if (!configId) {
      selectedSequenceId.value = sequenceId;
      selectedSequenceName.value =
        sequences.value?.find((seq) => seq.id === sequenceId)?.name || "";

      if (entityRowId) {
        rowContext.value = entityRowId;
        sequenceAddition.setDirectValidationData(1, "DIRECT");
      } else {
        const count = props.selection?.selected_ids?.length || 0;
        sequenceAddition.setDirectValidationData(count, "DIRECT");
      }

      sequenceAddition.setModalState("setupSenderDetailsModal", true);
      return;
    }

    const payload = {
      target: 1,
      selected_ids: entityRowId ? [entityRowId] : props.selection.selected_ids,
      sequence_id: sequenceId,
      enrich_phone: false,
    };

    if (configId) {
      payload.sequence_entity_config_id = configId;
    }

    const response = await DashboardService.addLeadsFromFind(payload);
    if (response?.success) {
      rowId.value = null;
      message.success(`Added to the ${featureNames.SEQUENCES} successfully`);
    } else if (response?.message) {
      message.error(response?.message);
    } else {
      message.error(`Failed to add to the ${featureNames.SEQUENCES}`);
    }
  } catch (error) {
    message.error(
      `An error occurred while adding items to the ${featureNames.SEQUENCES}`
    );
    console.error(
      `Error while updating the ${featureNames.SEQUENCES}: `,
      error
    );
  } finally {
    if (configId) {
      handleClearEntityState();
      rowContext.value = null;
      sequenceDropdown.value?.closeDropdown();
    }
  }
};

const handleSetupSenderDetailsSave = async (setupData) => {
  try {
    if (props.prospectData) {
      sequenceAddition.setModalState("setupSenderDetailsModal", false);
      await addApolloEntityToSequence({
        sequenceId: selectedSequenceId.value,
        rowId: rowId.value || rowContext?.value,
        configId: setupData.configId,
      });
    } else {
      validationType.value =
        sequenceAddition.state.pendingValidationData?.validation_type;

      const result = await sequenceAddition.handleSenderSetup(
        setupData,
        "validation"
      );

      if (result.success) {
        console.log("Setup sender details completed via composable");
      }
    }
  } catch (error) {
    console.error("Error in sender setup handler:", error);
    message.error("An error occurred during sender setup");
  }
};

const handleValidationAddToJourney = async (genericData) => {
  try {
    const result =
      await sequenceAddition.handleValidationCompletion(genericData);

    if (result.success) {
      if (isNewlyCreatedSequence.value && selectedSequenceId.value) {
        router.push({
          name: "sequence-edit",
          params: { id: selectedSequenceId.value },
        });
      }

      handleClearEntityState();
      emit("refresh");
      rowContext.value = null;

      isNewlyCreatedSequence.value = false;
    }
  } catch (error) {
    console.error("Error in validation completion:", error);
    isNewlyCreatedSequence.value = false;
  }
};

const fetchPendingResearch = async () => {
  const response =
    props.source === entityTypes.CONTACT
      ? await DashboardService.fetchPendingContactResearch()
      : await DashboardService.fetchPendingLeadResearch();
  if (response?.success) {
    pendingIds.value = response.pending_ids;
  }
};

const addApolloEntityToList = async ({ listId, rowId = null }) => {
  try {
    const payload = {
      target: 1,
      selected_ids: [rowId],
      list_id: listId,
      enrich_phone: false,
    };
    const response = await DashboardService.addLeadsFromFind(payload);
    if (response?.success) {
      message.success("Added to the list successfully");
    } else if (response?.message) {
      message.error(response?.message);
    } else {
      message.error("Failed to add to the list");
    }
  } catch (error) {
    message.error("An error occurred while adding items to the list");
    console.error("Error while updating the list: ", error);
  } finally {
    await getEntityList();
    handleClearEntityState();
    rowContext.value = null;
  }
};

watch(
  sequencesSearchQuery,
  (newValue) => {
    debouncedFetchSequences(newValue);
  },
  { immediate: true }
);

watch(
  () => props.selection,
  (newValue) => {
    if (newValue?.unselected_ids?.length === props.pagination?.total) {
      unselectAll();
    }
  },
  { immediate: true }
);

watch(
  () => sequenceAddition.state.modalStates.setupSenderDetailsModal,
  (newValue) => {
    if (!newValue) {
      sequenceAddition.handleSetupModalClose();
      isNewlyCreatedSequence.value = false;

      if (props.prospectData) {
        rowContext.value = null;
      }
    }
  }
);

const handleBulkEnrichEmail = async ({ selection, source }) => {
  try {
    let response = {};

    const payload = {
      select_all: selection.select_all,
      selected_ids: selection.selected_ids,
      unselected_ids: selection.unselected_ids,
      reveal_emails: true,
      reveal_phone_numbers: false,
    };

    if (props.viewId) {
      payload.view_id = props.viewId;
    }

    if (props.listId) {
      payload.list_id = props.listId;
    }
    const asyncEnabled = isAsyncBulkEnrichmentEnabled.value;

    if (source === entityTypes.CONTACT) {
      // Contacts path unchanged (server decides vendor)
      response = asyncEnabled
        ? await DashboardService.enrichContactsAsync(payload)
        : await DashboardService.enrichContacts(payload);
    } else if (source === entityTypes.LEAD) {
      response = asyncEnabled
        ? await DashboardService.enrichLeadsAsync(payload)
        : await DashboardService.enrichLeads(payload);
    }

    if (response?.success) {
      const validCount = response?.valid_contacts || response?.valid_leads || 0;
      const invalidCount =
        response?.invalid_contacts || response?.invalid_leads || 0;

      const toastMessage = `Enrichment initiated for ${validCount} ${entityText.value}${
        invalidCount >= 1
          ? `; skipped ${invalidCount} due to missing required data`
          : ""
      }`;

      moreDropdownRef.value?.closeDropdown();

      validCount === 0
        ? message.error(toastMessage)
        : message.success(toastMessage);

      unselectAll();
    } else {
      showAlreadyExistsMessage({ customMessage: response.message });
    }
  } catch (error) {
    handleApiError(error, message, (errorMessage) => {
      showAlreadyExistsMessage({ customMessage: errorMessage });
    });
  }
};

const handleBulkEnrichPhoneNumber = async ({ selection, source }) => {
  try {
    let response = {};

    const payload = {
      select_all: selection.select_all,
      selected_ids: selection.selected_ids,
      unselected_ids: selection.unselected_ids,
      reveal_emails: false,
      reveal_phone_numbers: true,
    };

    if (props.viewId) {
      payload.view_id = props.viewId;
    }

    if (props.listId) {
      payload.list_id = props.listId;
    }
    const asyncEnabled = isAsyncBulkEnrichmentEnabled.value;

    if (source === entityTypes.CONTACT) {
      response = asyncEnabled
        ? await DashboardService.enrichContactsAsync(payload)
        : await DashboardService.enrichContacts(payload);
    } else if (source === entityTypes.LEAD) {
      response = asyncEnabled
        ? await DashboardService.enrichLeadsAsync(payload)
        : await DashboardService.enrichLeads(payload);
    }

    if (response?.success) {
      const validCount = response?.valid_contacts || response?.valid_leads || 0;
      const invalidCount =
        response?.invalid_contacts || response?.invalid_leads || 0;
      const toastMessage = `Enrichment initiated for ${validCount} ${entityText.value}${
        invalidCount >= 1
          ? `; skipped ${invalidCount} due to missing required data`
          : ""
      }`;

      moreDropdownRef.value?.closeDropdown();

      validCount === 0
        ? message.error(toastMessage)
        : message.success(toastMessage);

      unselectAll();
    } else {
      showAlreadyExistsMessage({ customMessage: response.message });
    }
  } catch (error) {
    handleApiError(error, message, (errorMessage) => {
      showAlreadyExistsMessage({ customMessage: errorMessage });
    });
  }
};

const handleBulkEnrichCompanies = async (selection) => {
  try {
    const payload = {
      select_all: selection.select_all,
      selected_ids: selection.selected_ids,
      unselected_ids: selection.unselected_ids,
    };

    if (props.viewId) {
      payload.view_id = props.viewId;
    }

    if (props.listId) {
      payload.list_id = props.listId;
    }

    const response = await DashboardService.enrichCompanies(payload);

    if (response?.success) {
      const validCount = response?.valid_companies || 0;
      const invalidCount = response?.invalid_companies || 0;
      const toastMessage = `Enrichment initiated for ${validCount} ${entityText.value}${
        invalidCount >= 1
          ? `; skipped ${invalidCount} due to missing website information`
          : ""
      }`;

      moreDropdownRef.value?.closeDropdown();

      validCount === 0
        ? message.error(toastMessage)
        : message.success(toastMessage);

      unselectAll();
    } else {
      message.error("Failed to enrich companies");
    }
  } catch (error) {
    console.error("Error while enriching companies:", error);
    message.error("An error occurred while enriching companies");
  }
};

const handleBulkMarketResearch = async ({ selection, source }) => {
  try {
    let response = {};

    const payload = {
      select_all: selection.select_all,
      selected_ids: selection.selected_ids,
      unselected_ids: selection.unselected_ids,
    };

    if (props.viewId) {
      payload.view_id = props.viewId;
    }

    if (props.listId) {
      payload.list_id = props.listId;
    }

    if (source === entityTypes.CONTACT) {
      response = await DashboardService.marketResearchContacts(payload);
    } else if (source === entityTypes.LEAD) {
      response = await DashboardService.marketResearchLeads(payload);
    } else if (source === entityTypes.COMPANY) {
      response = await DashboardService.marketResearchCompanies(payload);
    }

    if (response?.success) {
      const validCount =
        response?.valid_contacts ||
        response?.valid_leads ||
        response?.valid_companies ||
        0;
      const invalidCount =
        response?.invalid_contacts ||
        response?.invalid_leads ||
        response?.invalid_companies ||
        0;
      const toastMessage = `Market research initiated for ${validCount} ${entityText.value}${
        invalidCount >= 1
          ? `; skipped ${invalidCount} due to missing required data`
          : ""
      }`;

      moreDropdownRef.value?.closeDropdown();

      validCount === 0
        ? message.error(toastMessage)
        : message.success(toastMessage);

      unselectAll();
    } else {
      message.error("Failed to generate market research");
    }
  } catch (error) {
    console.error("Error while generating market research:", error);
    message.error("An error occurred while generating market research");
  }
};

const bulkDelete = async () => {
  try {
    bulkDeleting.value = true;

    let response = {};

    const payload = {
      select_all: props.selection.select_all,
      selected_ids: props.selection.selected_ids,
      unselected_ids: props.selection.unselected_ids,
    };

    if (props.viewId) {
      payload.view_id = props.viewId;
    }

    if (props.listId) {
      payload.list_id = props.listId;
    }

    if (props.source === entityTypes.CONTACT) {
      response = await DashboardService.deleteContacts(payload);
    } else if (props.source === entityTypes.LEAD) {
      response = await DashboardService.deleteLeads(payload);
    } else if (props.source === entityTypes.COMPANY) {
      response = await DashboardService.deleteCompanies(payload);
    } else if (props.source === entityTypes.SEQUENCE) {
      response = await SequenceService.deleteMultipleSequences(payload);
    }

    if (response?.success) {
      message.success(`${entityTextWithCount.value} deleted successfully!`);
      emit("refresh");
      unselectAll();
    } else {
      message.error(`Failed to delete ${entityText.value}`);
    }
  } catch (error) {
    console.error("Error in bulk delete", error);
    message.error(`An error occurred while deleting ${entityText.value}`);
  } finally {
    bulkDeleteModalVisible.value = false;
    bulkDeleting.value = false;
  }
};

const bulkExport = async (data) => {
  try {
    let response = {};

    const payload = {
      select_all: props.selection.select_all,
      selected_ids: props.selection.selected_ids,
      unselected_ids: props.selection.unselected_ids,
      export_email: data?.email || "",
    };

    if (props.viewId) {
      payload.view_id = props.viewId;
    }

    if (props.listId) {
      payload.list_id = props.listId;
    }

    if (props.source === entityTypes.CONTACT) {
      response = await DashboardService.exportContacts(payload);
    } else if (props.source === entityTypes.LEAD) {
      response = await DashboardService.exportLeads(payload);
    } else if (props.source === entityTypes.COMPANY) {
      response = await DashboardService.exportCompanies(payload);
    }

    if (response?.success) {
      message.success(
        `${entityTextWithCount.value} will be exported. This will take a while...`
      );
      unselectAll();
      bulkExportModalVisible.value = false;
    } else {
      if (response?.limit_exceeded) {
        const errorMessage =
          response.message ||
          `Export limit exceeded (${response.total_count?.toLocaleString()} records selected, limit: ${response.export_limit?.toLocaleString()}). Please reduce your selection and try again.`;
        message.error(errorMessage);
      } else {
        message.error(`Failed to export ${entityText.value}`);
      }
    }
  } catch (error) {
    console.error(`Error while exporting ${entityText.value}:`, error);
  }
};

watch(
  () => showSequenceCreate.value,
  (newVal) => {
    if (newVal === false) {
      rowContext.value = null;
    }
  }
);

watch(
  () => showEntityCreate.value,
  (newVal) => {
    if (newVal === false) {
      rowContext.value = null;
    }
  }
);

onMounted(() => {
  if (hasListAccess.value) {
    getEntityList();
  }
  if (hasSequenceAccess.value) {
    fetchSequences();
  }
  fetchPendingResearch();
  isSequenceEnabled.value = localStorage.getItem("sequence") || false;
  loadAsyncBulkEnrichmentFlag();
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});

const editableRows = reactive({});

const registerEditableRow = (rowId, methods) => {
  editableRows[rowId] = methods;
};

const startEditing = (rowId) => {
  if (
    editableRows[rowId] &&
    typeof editableRows[rowId].startEdit === "function"
  ) {
    editableRows[rowId].startEdit();
  }
};

const cancelEditing = (rowId) => {
  if (
    editableRows[rowId] &&
    typeof editableRows[rowId].cancelEdit === "function"
  ) {
    editableRows[rowId].cancelEdit();
  }
};

const saveEditing = (rowId) => {
  if (
    editableRows[rowId] &&
    typeof editableRows[rowId].saveEdit === "function"
  ) {
    editableRows[rowId].saveEdit();
  }
};

const handleRowSave = async (updatedRow) => {
  try {
    const result = await emit("row-save", updatedRow);
    return result;
  } catch (error) {
    console.error("Error saving row:", error);
    return false;
  }
};

const handleRowEdit = (row) => {
  emit("row-edit", row);
};

const handleRowCancel = (row) => {
  if (row.isNewRow) {
    const updatedData = props.data.filter((item) => item.id !== row.id);

    emit("data-update", { type: "data", value: updatedData });
  }

  emit("row-cancel", row);
};

const triggerAddRow = () => {
  if (!props.addConfig || Object.keys(props.addConfig).length === 0) {
    console.warn("Add configuration is missing");
    return;
  }

  const newRow = {
    id: `new-${Date.now()}`,
    isNewRow: true,
  };

  if (props.addConfig.fields) {
    props.addConfig.fields.forEach((field) => {
      newRow[field.key] =
        field.defaultValue !== undefined ? field.defaultValue : "";
    });
  }

  const updatedData = [...props.data, newRow];
  emit("data-update", { type: "data", value: updatedData });

  if (props.addConfig.scrollToNew) {
    setTimeout(() => {
      const tableBody = document.querySelector(".table_body");
      if (tableBody) {
        tableBody.scrollTop = tableBody.scrollHeight;
      }
    }, 100);
  }
};

defineExpose({
  triggerAddRow,
  startEditing,
  cancelEditing,
  saveEditing,
  registerEditableRow,
});
</script>

<style>
.custom_table {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  color: var(--foreground-rgb);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  min-width: min-content;
  overflow: hidden;
}

.table_container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-x: scroll;
  overflow-y: hidden;
}

.table_head,
.table_row {
  display: grid;
  align-items: stretch;
}

.table_head {
  background-color: var(--white-opacity-4);
}

.table_body {
  flex: 1;
  background: transparent;
  overflow-y: scroll;
  overflow-x: hidden;
}

.table_header {
  display: flex;
  padding: 8px 12px;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  overflow: hidden;
  color: var(--secondary-color);
  text-overflow: ellipsis;
  font-family: "Proxima Nova", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.table_data,
.action_wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 8px;
  align-self: stretch;
  overflow: hidden;
  color: var(--foreground-rgb);
  text-overflow: ellipsis;
  font-family: "Proxima Nova", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
}

.action_wrapper {
  justify-content: flex-end;
}

.action_column_header {
  padding: 8px 12px;
  background-color: var(--white-opacity-4);
  color: var(--secondary-color);
  font-family: "Proxima Nova", sans-serif;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  box-sizing: border-box;
}

.action_column {
  background-color: transparent;
  display: flex;
  flex-direction: column;
}

.table_row {
  border-bottom: 1px solid var(--white-opacity-12);
}

@media (max-width: 768px) {
  .table_container {
    overflow-x: auto;
  }
  .table_data {
    flex-direction: column;
    align-items: flex-start;
  }
}

.sorting_applied {
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 6px;
  background: var(--white-opacity-4);
  width: 16px;
  height: 18px;
}

.table_wrapper::-webkit-scrollbar {
  width: 8px;
}
.table_wrapper::-webkit-scrollbar-track {
  background: transparent;
}
.table_wrapper::-webkit-scrollbar-thumb {
  background-color: var(--white-opacity-12);
  border-radius: 4px;
}

.table_wrapper {
  scrollbar-width: thin;
  scrollbar-color: var(--white-opacity-12) transparent;
  border: 1px solid var(--white-opacity-12);
  border-radius: 8px;
}

.table_wrapper {
  -ms-overflow-style: none;
}

.table_wrapper::-ms-scrollbar {
  width: 8px;
}

.table_wrapper::-ms-scrollbar-track {
  background: transparent;
}

.table_wrapper::-ms-scrollbar-thumb {
  background-color: var(--white-opacity-12);
  border-radius: 4px;
}

.bulk_remove_modal_overlay {
  display: flex !important;
  align-items: flex-start !important;
  justify-content: center !important;
  padding-top: 70px !important;
}
</style>
